import React, { useMemo } from "react";
import { StreamingProvider } from "@switcherstudio/switcher-api-client";
import styles from "./index.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { PlatformId } from "views/page-content/platforms/types";
import { Link } from "components/buttons/Link";
import { Trans, useTranslation } from "react-i18next";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { useCombineLoading } from "hooks/useCombineLoading";
import { Spinner } from "components/spinners/Spinner";
import { DestinationCard } from "./destinations/DestinationCard";
import { DestinationDisplay } from "./destinations/DestinationDisplay";
import { CatalogDestination } from "./destinations/CatalogDestination";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import { updateGeneralForm } from "store/platforms/slice";
import PencilIcon from "assets/icons/pencil-simple.svg?react";
import { AppDispatch } from "store/store";

interface SelectDestinationStepProps {
    setSelectedPlatform: (platform: PlatformId | null) => void;
    advanceToPlatformSettings: (destination?: DestinationDescriptor) => void;
}

export const SelectDestinationStep = ({
    setSelectedPlatform,
    advanceToPlatformSettings
}: SelectDestinationStepProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const user = useSelector((state: RootState) => state.user);

    const platforms = useSelector((s: RootState) => s.platforms);
    const generalForm = platforms.general.generalForm;

    const userHasHitMaxDests = useMemo(() => {
        const userMaxDests =
            user.userInfo?.Claims?.Details?.FeatureLimitClaims?.[
                "cloudflaresimulcasting:maxdestinations"
            ];

        const numberOfSelectedDests =
            generalForm.selectedSimulcastDestinations.filter(
                (d) => d.platform !== PlatformId.VideoPlayer
            ).length;

        return (
            userMaxDests &&
            userMaxDests > 0 &&
            numberOfSelectedDests >= userMaxDests
        );
    }, [user.userInfo, generalForm.selectedSimulcastDestinations]);

    const {
        transformedData: streamingProviders,
        loading: streamingProvidersLoading
    } = useSwitcherClient(
        (client) => client.streamingProviders_GetStreamingProviders,
        {
            requestImmediately: true,
            transformResponseData: ({ originalResponse }) => {
                const supportedPlatforms = [
                    PlatformId.Facebook,
                    PlatformId.Youtube,
                    PlatformId.Twitch,
                    PlatformId.VideoPlayer,
                    PlatformId.AmazonLive,
                    PlatformId.Instafeed,
                    PlatformId.Caast,
                    PlatformId.MsStream
                ];
                return originalResponse.StreamingProviders.filter((p) =>
                    supportedPlatforms.includes(p.Id as PlatformId)
                );
            }
        }
    );

    const { transformedData: rtmpChannels, loading: rtmpChannelsLoading } =
        useSwitcherClient(
            (client) =>
                client.switcherStreamSettings_GetAllSwitcherStudioSettings,
            {
                requestImmediately: true,
                transformResponseData: ({ originalResponse }) => {
                    return originalResponse;
                }
            }
        );

    const availableExternalPlatforms = useMemo(() => {
        const streamingProvidersWithoutSwitcher = streamingProviders?.filter(
            (p) => p.Id !== PlatformId.VideoPlayer
        );

        const availableExternalPlatforms = [
            ...(streamingProvidersWithoutSwitcher || []),
            {
                Id: PlatformId.Rtmp,
                Name: "RTMP",
                IsLinked: !!rtmpChannels?.length
            }
        ].sort((a, b) => Number(b.IsLinked) - Number(a.IsLinked));

        return availableExternalPlatforms;
    }, [streamingProviders, rtmpChannels]);

    /**
     * A provider should be disabled if it is a single use provider
     * and already added to the destinations, or if it is not linked
     */
    const isPlatformDisabled = (p: StreamingProvider) => {
        const singleUseProviders: PlatformId[] = [
            PlatformId.VideoPlayer,
            PlatformId.AmazonLive,
            PlatformId.Caast,
            PlatformId.Instafeed,
            PlatformId.MsStream
        ];

        const isSingleUse = singleUseProviders.includes(p.Id as PlatformId);
        const alreadyAdded = generalForm.selectedSimulcastDestinations.some(
            (d) => d.platform === p.Id
        );

        return isSingleUse ? alreadyAdded : !p.IsLinked;
    };

    const platformClickHandler = (platform: StreamingProvider) => {
        setSelectedPlatform(platform.Id as PlatformId);
        advanceToPlatformSettings();
    };

    const removeDestination = (destination: DestinationDescriptor) => {
        dispatch(
            updateGeneralForm({
                selectedSimulcastDestinations:
                    generalForm.selectedSimulcastDestinations.filter(
                        (d) => d.id !== destination.id
                    )
            })
        );
    };

    const loading = useCombineLoading(
        streamingProvidersLoading,
        rtmpChannelsLoading
    );

    if (loading) {
        return <Spinner />;
    }

    return (
        <div className={styles["select-destination-step"]}>
            <section>
                <h6>{t("simulcast:select-destinations")}</h6>
                <CatalogDestination />
                <DestinationDisplay
                    destinations={generalForm.selectedSimulcastDestinations}
                    actions={[
                        {
                            type: "icon",
                            onClick: (destination) => {
                                setSelectedPlatform(
                                    destination.platform as PlatformId
                                );
                                advanceToPlatformSettings(destination);
                            },
                            element: <PencilIcon />
                        },
                        {
                            type: "remove",
                            onClick: (destination) =>
                                removeDestination(destination),
                            element: <>{t("buttons:remove")}</>
                        }
                    ]}
                />
            </section>
            <section>
                <h6>{t("simulcast:select-simulcast-destination")}</h6>
                <small
                    className={
                        userHasHitMaxDests ? styles["limit-warning"] : ""
                    }
                >
                    <p>
                        {userHasHitMaxDests ? (
                            t("simulcast:limit-hit")
                        ) : (
                            <Trans
                                i18nKey="simulcast:connect-platform-cta"
                                components={{
                                    link1: <Link to="/manage-platforms" />
                                }}
                            />
                        )}
                    </p>
                </small>
                <div className={styles["destinations-wrapper"]}>
                    {availableExternalPlatforms?.map((ap, i) => {
                        return (
                            <DestinationCard
                                platform={ap}
                                key={i}
                                isDisabled={
                                    isPlatformDisabled(ap) || userHasHitMaxDests
                                }
                                clickHandler={() => platformClickHandler(ap)}
                            />
                        );
                    })}
                </div>
            </section>
        </div>
    );
};
